import { Card, Tag } from "@/frontend/components/common";
import type { TournamentResponseDTO } from "@/shared/dto";
import type { SeasonInfo } from "../types";
import { ConfigBadge, FormatStagesPreview } from "../components";
import { Calendar } from "antd";

interface OverviewTabProps {
  tournament: TournamentResponseDTO;
  currentSeason?: SeasonInfo;
}

export function OverviewTab({ tournament, currentSeason }: OverviewTabProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
      {/* Información General */}
      <Card title="Información General" className="shadow-sm">
        <dl className="space-y-3">
          {tournament.description && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Descripción</dt>
              <dd className="mt-1 text-gray-900">{tournament.description}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">Tipo</dt>
            <dd className="mt-1 text-gray-900">{tournament.type}</dd>
          </div>
          {currentSeason?.format && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Formato</dt>
              <dd className="mt-1 text-gray-900">
                <span className="font-medium">{currentSeason.format.name}</span>
              </dd>
            </div>
          )}
          {currentSeason && (
            <>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Temporada Actual
                </dt>
                <dd className="mt-1">
                  <Tag color="blue">{currentSeason.season}</Tag>
                </dd>
              </div>
              {(currentSeason.start || currentSeason.end) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fechas</dt>
                  <dd className="mt-1 text-sm text-gray-600">
                    {currentSeason.start && (
                      <div>
                        Inicio:{" "}
                        <strong>
                          {new Date(currentSeason.start).toLocaleDateString(
                            "es-ES",
                          )}
                        </strong>
                      </div>
                    )}
                    {currentSeason.end && (
                      <div>
                        Fin:{" "}
                        <strong>
                          {new Date(currentSeason.end).toLocaleDateString(
                            "es-ES",
                          )}
                        </strong>
                      </div>
                    )}

                    {/* Pequeña vista de calendario dentro de Información General */}
                    {currentSeason.start && currentSeason.end && (
                      <div className="mt-4 flex justify-center">
                        <Calendar
                          fullscreen={false}
                          disabledDate={(current) => {
                            const start = new Date(currentSeason.start!);
                            const end = new Date(currentSeason.end!);
                            return current < start || current > end;
                          }}
                        />
                      </div>
                    )}
                  </dd>
                </div>
              )}
            </>
          )}
        </dl>
      </Card>

      {/* Calendario */}
      {currentSeason?.start && currentSeason?.end && (
        <Card title="Calendario" className="shadow-sm">
          <div className="flex justify-center">
            <Calendar
              fullscreen={false}
              disabledDate={(current) => {
                const start = new Date(currentSeason.start!);
                const end = new Date(currentSeason.end!);
                return current < start || current > end;
              }}
            />
          </div>
        </Card>
      )}

      {/* Configuración */}
      <Card title="Configuración" className="shadow-sm">
        {tournament.configuration ? (
          <div className="flex flex-wrap gap-2">
            <ConfigBadge
              label="Árbitros"
              active={tournament.configuration.hasReferees}
            />
            <ConfigBadge
              label="Alineaciones"
              active={tournament.configuration.hasLineups}
            />
            <ConfigBadge
              label="Estadio"
              active={tournament.configuration.hasStadium}
            />
            <ConfigBadge
              label="Equipos"
              active={tournament.configuration.hasTeams}
            />
            <ConfigBadge
              label="Sustituciones"
              active={tournament.configuration.hasSubstitutions}
            />
            <ConfigBadge
              label="Tarjetas"
              active={tournament.configuration.hasCards}
            />
          </div>
        ) : (
          <p className="text-gray-500 italic">Sin configuración definida</p>
        )}
        {tournament.configuration?.maxPlayersPerTeam && (
          <p className="mt-3 text-sm text-gray-600">
            Máx. jugadores por equipo:{" "}
            <strong>{tournament.configuration.maxPlayersPerTeam}</strong>
          </p>
        )}
        {tournament.configuration?.minPlayersPerTeam && (
          <p className="text-sm text-gray-600">
            Mín. jugadores por equipo:{" "}
            <strong>{tournament.configuration.minPlayersPerTeam}</strong>
          </p>
        )}
      </Card>

      {/* Temporadas */}

      {/* Formato del torneo - Etapas */}
      {currentSeason?.format?.config && (
        <FormatStagesPreview config={currentSeason.format.config} />
      )}
    </div>
  );
}
